import React, { useState } from "react";
import "./ArgusReport.css";
import data from "./ArgusReport.json";
import {
  LineChart,
  Line,
  ResponsiveContainer
} from "recharts";
import Select from 'react-select';

/* ======================
   MOCK DATA
====================== */

const apiData = data;
const num = (v) => Number(v || 0);
const weeklyData = apiData.trends;
const journalData = apiData.journals;

const slicerOptions = {
  division: apiData.filters.division,
  market: apiData.filters.market,
  hospital: apiData.filters.hospital,
  ffc: apiData.filters.ffc,
  week: apiData.filters.week
};

/* ======================
   BIG KPI CARD
====================== */

const BigCard = ({ title, value, goal, trendData, color }) => {
    const pct = Math.min(num(value) / goal, 1);
    const pctDisplay = Math.round(pct * 100);
  
    return (
      <div className="argus-tile">
  
        {/* LEFT SIDE */}
        <div className="argus-tile-left">
          <div className="argus-tile-title-row">
            <span className="argus-tile-title">{title}</span>
  
            <span
              className="argus-tile-badge"
              style={{
                background: pct > 0.85 ? "#16a34a" : "#6366f1"
              }}
            >
              {pctDisplay}%
            </span>
          </div>
  
          <div className="argus-tile-value">
            ${value.toLocaleString()}
          </div>
  
          <div className="argus-tile-sub">
            <span><strong>+4%</strong> WoW</span>
            <span><strong>+9%</strong> YoY</span>
            <span><strong>82%</strong> YTD</span>
          </div>
        </div>
  
        {/* RIGHT SIDE → SPARKLINE */}
        <div className="argus-tile-right">
          <ResponsiveContainer width="100%" height={60}>
            <LineChart data={trendData}>
              <Line
                dataKey="value"
                stroke={color}
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
  
      </div>
    );
  };

  /* SMALL KPI CARD */
  const SmallKPI = ({ title, value, trendData, color }) => (
    <div className="argus-small-tile">
  
      <div className="argus-small-tile-left">
        <div className="argus-small-tile-title-row">
          <span className="argus-small-tile-title">{title}</span>
        </div>

        <div className="argus-small-tile-value">
          ${value.toLocaleString()}
        </div>

      </div>

      <div className="argus-small-tile-right">
        <ResponsiveContainer width="100%" height={60}>
          <LineChart data={trendData}>
            <Line
              dataKey="value"
              stroke={color}
              strokeWidth={2.5}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

    </div>
  );

const ArgusReport = () => {

  /* SLICER STATE */
  const [division, setDivision] = useState([]);
  const [market, setMarket] = useState([]);
  const [hospital, setHospital] = useState([]);
  const [ffc, setFfc] = useState([]);
  const [week, setWeek] = useState([]);
  const [tableView, setTableView] = useState("FFC");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "recovered", dir: "desc" });
  const [journalTab, setJournalTab] = useState("ESCALATION");

  const tableData =
  tableView === "FFC"
    ? apiData.ffc_table
    : tableView === "HOSPITAL"
    ? apiData.hospital_table
    : apiData.item_table;

  const filteredTable = tableData
  .filter(row =>
    (row.name ?? "").toLowerCase().includes(searchTerm.toLowerCase())
  )
  .map(r => ({
    ...r,    
    revPerPatient: num(r.patients) ? num(r.recovered) / num(r.patients) : 0,
    journalsPerPatient: num(r.patients) ? num(r.journals) / num(r.patients) : 0,
    revPerJournal: num(r.journals) ? num(r.recovered) / num(r.journals) : 0,
    recoveryRate: (num(r.recovered) + num(r.missed))
      ? num(r.recovered) / (num(r.recovered) + num(r.missed))
      : 0
  }))
  .sort((a, b) => {
    const val = num(a[sortConfig.key]) - num(b[sortConfig.key]);
    return sortConfig.dir === "asc" ? val : -val;
  });

  const filteredJournals = journalData.filter(j => {
    if (journalTab === "ESCALATION") return j.is_escalated;
    if (journalTab === "LOCAL") return j.is_local_leadership;
    if (journalTab === "PRICING") return j.is_pricing;
    return false;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc"
    }));
  };  

  /* AGGREGATES */
  const totals = {
    recovered: num(apiData.summary.recovered),
    missed: num(apiData.summary.missed),
    journals: num(apiData.summary.journals),
    patients: num(apiData.summary.patients)  
  };  

  return (
    <div className="argus-report-page">

      {/* HEADER */}
        <div className="argus-controls">

            <div className="argus-title">
                Argus Performance Dashboard
            </div>
            <div className="argus-filters">
                <Select
                  isMulti
                  placeholder="Division"
                  options={slicerOptions.division.map(d => ({ label: d, value: d }))}
                  value={division.map(d => ({ label: d, value: d }))}
                  onChange={(s) => setDivision(s ? s.map(x => x.value) : [])}
                  className="argus-select"
                  classNamePrefix="custom-select"
                />

                <Select
                  isMulti
                  placeholder="Market"
                  options={slicerOptions.market.map(d => ({ label: d, value: d }))}
                  value={market.map(d => ({ label: d, value: d }))}
                  onChange={(s) => setMarket(s ? s.map(x => x.value) : [])}
                  className="argus-select"
                  classNamePrefix="custom-select"
                />

                <Select
                  isMulti
                  placeholder="Hospital"
                  options={slicerOptions.hospital.map(d => ({ label: d, value: d }))}
                  value={hospital.map(d => ({ label: d, value: d }))}
                  onChange={(s) => setHospital(s ? s.map(x => x.value) : [])}
                  className="argus-select"
                  classNamePrefix="custom-select"
                />

                <Select
                  isMulti
                  placeholder="FFC"
                  options={slicerOptions.ffc.map(d => ({ label: d, value: d }))}
                  value={ffc.map(d => ({ label: d, value: d }))}
                  onChange={(s) => setFfc(s ? s.map(x => x.value) : [])}
                  className="argus-select"
                  classNamePrefix="custom-select"
                />

                <Select
                  isMulti
                  placeholder="Fiscal Week"
                  options={slicerOptions.week.map(d => ({ label: d, value: d }))}
                  value={week.map(d => ({ label: d, value: d }))}
                  onChange={(s) => setWeek(s ? s.map(x => x.value) : [])}
                  className="argus-select"
                  classNamePrefix="custom-select"
                />
            </div>
        </div>


      {/* BIG CARDS */}
      <div className="argus-card-row">

        <BigCard
          title="Revenue Recovered"
          value={totals.recovered}
          goal={200000}
          color="#22c55e"
          trendData={weeklyData.map(w => ({ value: w.recovered }))}
        />

        <BigCard
          title="Visits Checked"
          value={totals.patients}
          goal={5000}
          color="#3b82f6"
          trendData={weeklyData.map(w => ({ value: w.patients }))}
        />

        <BigCard
          title="Journals Written"
          value={totals.journals}
          goal={2000}
          color="#a855f7"
          trendData={weeklyData.map(w => ({ value: w.journals }))}
        />

        <BigCard
          title="Unrecovered Revenue"
          value={totals.missed}
          goal={50000}
          color="#ef4444"
          trendData={weeklyData.map(w => ({ value: w.missed }))}
        />

      </div>

      <div className="argus-lower">

        {/* LEFT: TABLE */}
        <div className="argus-table-section">

        <div className="argus-table-header">
            <div className="argus-table-title">
            {`${tableView} Performance`}
            </div>


            <div className="argus-segmented">
              {["FFC", "HOSPITAL", "LINE"].map(opt => (
                <button
                  key={opt}
                  className={tableView === opt ? "active" : ""}
                  onClick={() => setTableView(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>

        </div>

        <div className="argus-table-wrapper">
            
            <input
              className="argus-table-search"
              placeholder={`Search ${tableView.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
  
            <table className="argus-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("name")}>
                  {tableView}
                </th>
                <th onClick={() => handleSort("recovered")}>Recovered {sortConfig.key === "recovered" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>
                <th onClick={() => handleSort("patients")}>Visits {sortConfig.key === "patients" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>
                <th onClick={() => handleSort("journals")}>Journals {sortConfig.key === "journals" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>
                <th onClick={() => handleSort("missed")}>Unrecovered {sortConfig.key === "missed" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>

                <th onClick={() => handleSort("revPerPatient")}>Rev / Pt {sortConfig.key === "revPerPatient" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>
                <th onClick={() => handleSort("journalsPerPatient")}>Jrn / Pt {sortConfig.key === "journalsPerPatient" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>
                <th onClick={() => handleSort("revPerJournal")}>Rev / Jrn {sortConfig.key === "revPerJournal" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>
                <th onClick={() => handleSort("recoveryRate")}>Recovery % {sortConfig.key === "recoveryRate" ? (sortConfig.dir === "asc" ? "↑" : "↓") : ""}</th>
              </tr>
            </thead>

            <tbody>
              {filteredTable.map((r, i) => (
                <tr key={i}>
                  <td>{r.name}</td>
                  <td>${num(r.recovered).toLocaleString()}</td>
                  <td>{num(r.patients).toLocaleString()}</td>
                  <td>{num(r.journals).toLocaleString()}</td>
                  <td>${num(r.missed).toLocaleString()}</td>
                  <td>${(r.revPerPatient ?? 0).toFixed(2)}</td>
                  <td>{(r.journalsPerPatient ?? 0).toFixed(2)}</td>
                  <td>${(r.revPerJournal ?? 0).toFixed(2)}</td>
                  
                  <td style={{
                    color: r.recoveryRate > 0.8 ? "#16a34a" : "#dc2626",
                    fontWeight: 600
                  }}>
                  {((r.recoveryRate ?? 0) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        </div>

        {/* RIGHT: KPI STACK */}
        <div className="argus-mini-section">
          <div className="argus-mini-top">
            <SmallKPI
                title="Revenue / Visit"
                value={`${Number(apiData.summary.revenue_per_patient || 0).toFixed(2)}`}
                color="#22c55e"                
                trendData={weeklyData.map(w => ({
                  value: w.patients ? num(w.recovered) / num(w.patients) : 0
                }))}
            />

            <SmallKPI
                title="Journals / Visit"
                value={Number(apiData.summary.journals_per_patient || 0).toFixed(2)}
                color="#3b82f6"
                trendData={weeklyData.map(w => ({
                  value: w.patients ? num(w.journals) / num(w.patients) : 0
                }))}
            />

            <SmallKPI
                title="Revenue / Journal"
                value={`${Number(apiData.summary.revenue_per_journal || 0).toFixed(2)}`}
                color="#a855f7"                
                trendData={weeklyData.map(w => ({
                  value: w.journals ? num(w.recovered) / num(w.journals) : 0
                }))}
            />

            <SmallKPI
                title="Recovery Rate"
                value={`${num(apiData.summary.recovery_rate).toFixed(1)}%`}
                color="#f59e0b"
                trendData={weeklyData.map(w => ({
                  value: (num(w.recovered) + num(w.missed))
                    ? num(w.recovered) / (num(w.recovered) + num(w.missed))
                    : 0
                }))}             
            />
          </div>
        <div className="argus-journal-panel">
          <div className="argus-journal-title">Journal Details Alerting</div>
        {/* TABS */}
        <div className="argus-journal-tabs">
          {[
            { key: "ESCALATION", label: "Escalation" },
            { key: "LOCAL", label: "Local Leadership" },
            { key: "PRICING", label: "Pricing" }
          ].map(tab => (
            <button
              key={tab.key}
              className={journalTab === tab.key ? "active" : ""}
              onClick={() => setJournalTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* LIST */}
        <ul className="argus-journal-list">
          {filteredJournals.map((j, i) => {

            let text;

            if (journalTab === "PRICING") {
              text = `${j.hospital} - ${j.invoiceitemid} - ${j.label} - $${j.expected_charge} - ${j.note}`;
            } else {
              text = `${j.hospital} - ${j.staff} - ${j.invoiceitemid} - ${j.label} - ${j.note}`;
            }

            return <li key={i}>{text}</li>;
          })}
        </ul>

        </div>

        </div>

      </div>
    </div>
  );
};

export default ArgusReport;